import { GetStaticProps, GetStaticPropsContext } from 'next';
import { RichText } from 'prismic-reactjs';
import React from 'react';
import dynamic from 'next/dynamic';

import {
  apolloClient,
  defaultQueryResult,
  getPageQuery,
} from 'utils/apolloClient';
import { getCurrentLocale } from 'utils/getCurrentLocale';

import { CLIENTS_QUERY } from '../graphql/queries';

import Cooperation from 'styledComponents/Cooperation';
import { prepareSeoFromPageData } from '../components/core/SEO/interface';
import { ClientType } from '../models/pageTypes/clients';
import { Page } from '../models/pageTypes/page';
import Hero from 'components/sections/Hero';
import { Wrapper } from '../styles/pages/styled/clients.styles';
import SectionDescriptionWithImage from '../components/sections/SectionDescriptionWithImage';
import { TitleAs } from 'models/enums/titleAs';

const DynamicBusinessGoals = dynamic(
  () => import('../components/sections/BusinessGoals')
);
const DynamicReviews = dynamic(() => import('../components/sections/Reviews'));
const DynamicTrustedBy = dynamic(
  () => import('../components/sections/TrustedBy')
);
const DynamicProjects = dynamic(
  () => import('../components/sections/Projects')
);

const Clients = ({ data: { page } }: Page<ClientType>) => {
  return (
    <Wrapper className={'clients'}>
      <Hero
        title={RichText.asText(page.title)}
        lottieProps={{
          id: 'clients__hero_lottie',
          src: 'https://assets10.lottiefiles.com/packages/lf20_uctf2rw6.json',
        }}
      />

      <SectionDescriptionWithImage
        title={RichText.asText(page.introduction_title)}
        subtitle={RichText.asText(page.introduction_title2)}
        description={RichText.asText(page.introduction_text)}
        image={page.introduction_image}
        isImageToRight
        titleAs={TitleAs.h2}
      />

      <DynamicProjects
        items={page.projects}
        title={RichText.asText(page.projects_title)}
        description={page.projects_description}
        titleAs={TitleAs.h3}
      />

      <DynamicBusinessGoals
        data={{
          title: RichText.asText(page.business_title),
          title2: RichText.asText(page.business_title2),
          image: page.business_image,
          points: page.business_goals,
        }}
        titleAs={TitleAs.h4}
      />

      <Cooperation
        title={RichText.asText(page.cooperation_title)}
        description={page.cooperation_description}
        description2={page.cooperation_description2}
        link={page.cooperation_contact_link}
        text={page.cooperation_contact_text}
        titleAs={TitleAs.h5}
      />

      <DynamicTrustedBy
        companiesTitle={RichText.asText(page.companies_title)}
        companies={page.clients_companies}
      />

      <DynamicReviews
        data={{
          title: RichText.asText(page.reviews_title),
          title2: RichText.asText(page.reviews_title2),
          description: RichText.asText(page.reviews_description),
          clutch: RichText.asText(page.clutch_text),
          reviews: page.reviews,
        }}
        titleAs={TitleAs.h6}
      />
    </Wrapper>
  );
};

export const getStaticProps: GetStaticProps = async (
  context: GetStaticPropsContext
) => {
  const currentLanguage: string = getCurrentLocale(context);

  const {
    data: { allClients_pages, ...defaultData },
  } = await apolloClient.query(
    getPageQuery(
      {
        pageLang: currentLanguage,
      },
      'careers_query',
      CLIENTS_QUERY
    )
  );

  return {
    props: {
      data: {
        ...defaultQueryResult(defaultData),
        seo: prepareSeoFromPageData(allClients_pages.edges[0].node),
        page: allClients_pages.edges[0].node,
      },
    },
  };
};

export default Clients;
